import React, { useMemo } from 'react'
import { FormattedDate, FormattedMessage } from 'react-intl'

import styled, { useTheme } from 'styled-components/native'

import { BodyText } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { CircleIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/CircleIcon'
import { LoadingSkeleton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/loadingSkeleton/LoadingSkeleton'
import { ActivityStatus } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/clientFilters/ClientFiltersPopover'
import { BodyTextSize } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/typeStyles'

export type ActivityStatusItem = {
  status: ActivityStatus | null
  sendDate: Date | null
}

type ClientListActivityInfoProps = {
  activityStatus?: ActivityStatusItem
}

const Container = styled.View({
  flexDirection: 'column',
})

const Row = styled.View(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing['4px'],
}))

const ClientListActivityInfo: React.FC<ClientListActivityInfoProps> = ({ activityStatus }) => {
  const theme = useTheme()
  const textColor = useMemo(() => {
    switch (activityStatus?.status) {
      case ActivityStatus.INCOMPLETE:
        return theme.colors.textWarning
      case ActivityStatus.COMPLETE:
      case ActivityStatus.PENDING:
        return theme.colors.textPrimary
      case ActivityStatus.NOT_SENT:
      default:
        return theme.colors.textSecondary
    }
  }, [activityStatus?.status, theme.colors.textSecondary, theme.colors.textWarning, theme.colors.textPrimary])

  const text = useMemo(() => {
    switch (activityStatus?.status) {
      case ActivityStatus.INCOMPLETE:
        return <FormattedMessage defaultMessage='Incomplete' description='Incomplete activity status' />
      case ActivityStatus.COMPLETE:
        return <FormattedMessage defaultMessage='Complete' description='Complete activity status' />
      case ActivityStatus.PENDING:
        return <FormattedMessage defaultMessage='Pending' description='Pending activity status' />
      case ActivityStatus.NOT_SENT:
        return <FormattedMessage defaultMessage='None sent' description='Not sent activity status' />
      default:
        return '-'
    }
  }, [activityStatus?.status])

  if (!activityStatus) {
    return <LoadingSkeleton width={100} height={40} />
  }

  return (
    <Container>
      <Row>
        {activityStatus.status === ActivityStatus.INCOMPLETE && (
          <CircleIcon isFilled size={8} fillColor={theme.colors.iconWarning} />
        )}
        <BodyText text={text} color={textColor} />
      </Row>
      {activityStatus.sendDate && (
        <BodyText
          size={BodyTextSize.SMALL}
          text={
            <FormattedMessage
              defaultMessage='Sent {sendDate}'
              description='Send date'
              values={{
                sendDate: (
                  <FormattedDate value={activityStatus.sendDate} year='numeric' month='numeric' day='numeric' />
                ),
              }}
            />
          }
          color={theme.colors.textSecondary}
        />
      )}
    </Container>
  )
}

export default ClientListActivityInfo
